import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

export interface Step {
  id: string;
  number: number;
  description: string;
  category: 'user' | 'frontend' | 'network' | 'backend' | 'database' | 'storage' | 'response';
  status: 'pending' | 'running' | 'success' | 'error' | 'skipped';
  duration?: number;
  details?: string;
  timestamp?: number;
  subSteps?: Step[];
}

interface StepTrackerProps {
  title: string;
  steps: Step[];
  onStepUpdate?: (stepId: string, status: Step['status'], details?: string) => void;
  showTimeline?: boolean;
  showCategories?: boolean;
  compact?: boolean;
}

export const StepTracker: React.FC<StepTrackerProps> = ({
  title,
  steps,
  onStepUpdate,
  showTimeline = true,
  showCategories = true,
  compact = false
}) => {
  const [startTime, setStartTime] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);

  useEffect(() => {
    const firstRunning = steps.find(s => s.status === 'running');
    if (firstRunning && !startTime) {
      setStartTime(Date.now());
    }
  }, [steps, startTime]);

  useEffect(() => {
    if (startTime) {
      const interval = setInterval(() => {
        setCurrentTime(Date.now() - startTime);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [startTime]);

  const getCategoryColor = (category: Step['category']) => {
    const colors = {
      user: 'bg-purple-100 text-purple-700 border-purple-300',
      frontend: 'bg-blue-100 text-blue-700 border-blue-300',
      network: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      backend: 'bg-orange-100 text-orange-700 border-orange-300',
      database: 'bg-green-100 text-green-700 border-green-300',
      storage: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      response: 'bg-pink-100 text-pink-700 border-pink-300'
    };
    return colors[category];
  };

  const getStatusIcon = (status: Step['status']) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'running': return '⚡';
      case 'skipped': return '⏭️';
      default: return '⏳';
    }
  };

  const getStatusColor = (status: Step['status']) => {
    switch (status) {
      case 'success': return 'bg-green-50 border-green-200';
      case 'error': return 'bg-red-50 border-red-200';
      case 'running': return 'bg-blue-50 border-blue-200 animate-pulse';
      case 'skipped': return 'bg-gray-50 border-gray-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const calculateProgress = () => {
    const completed = steps.filter(s => s.status === 'success' || s.status === 'skipped').length;
    return (completed / steps.length) * 100;
  };

  const getElapsedTime = (step: Step) => {
    if (!step.timestamp || !startTime) return null;
    return ((step.timestamp - startTime) / 1000).toFixed(2);
  };

  const renderStep = (step: Step, index: number, isSubStep = false) => (
    <motion.div
      key={step.id}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`${isSubStep ? 'ml-8' : ''} mb-2`}
    >
      <div className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${getStatusColor(step.status)}`}>
        {/* Step Number */}
        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-white border-2 font-bold text-sm">
          {step.number}
        </div>

        {/* Status Icon */}
        <div className="flex-shrink-0 text-xl">
          {getStatusIcon(step.status)}
        </div>

        {/* Step Details */}
        <div className="flex-grow">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium">{step.description}</span>
            {showCategories && (
              <span className={`px-2 py-0.5 text-xs rounded-full border ${getCategoryColor(step.category)}`}>
                {step.category}
              </span>
            )}
          </div>
          {step.details && (
            <div className="text-sm text-gray-600 mt-1">{step.details}</div>
          )}
          {step.subSteps && (
            <div className="mt-2 space-y-1">
              {step.subSteps.map((subStep, i) => renderStep(subStep, i, true))}
            </div>
          )}
        </div>

        {/* Timing */}
        {showTimeline && step.timestamp && (
          <div className="flex-shrink-0 text-xs text-gray-500">
            +{getElapsedTime(step)}s
          </div>
        )}
      </div>
    </motion.div>
  );

  if (compact) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">{title}</h3>
          <span className="text-sm text-gray-500">
            {steps.filter(s => s.status === 'success').length}/{steps.length} complete
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${calculateProgress()}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">{title}</h2>
        <div className="flex items-center gap-4">
          {startTime > 0 && (
            <div className="text-sm text-gray-600">
              Time: {(currentTime / 1000).toFixed(1)}s
            </div>
          )}
          <div className="text-sm font-medium">
            Progress: {steps.filter(s => s.status === 'success').length}/{steps.length}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${calculateProgress()}%` }}
          />
        </div>
      </div>

      {/* Category Legend */}
      {showCategories && (
        <div className="flex flex-wrap gap-2 mb-4">
          {['user', 'frontend', 'network', 'backend', 'database', 'storage', 'response'].map(cat => (
            <span
              key={cat}
              className={`px-3 py-1 text-xs rounded-full border ${getCategoryColor(cat as Step['category'])}`}
            >
              {cat}
            </span>
          ))}
        </div>
      )}

      {/* Steps */}
      <div className="space-y-2">
        {steps.map((step, index) => renderStep(step, index))}
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-green-600">
              {steps.filter(s => s.status === 'success').length}
            </div>
            <div className="text-xs text-gray-600">Completed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {steps.filter(s => s.status === 'running').length}
            </div>
            <div className="text-xs text-gray-600">Running</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-600">
              {steps.filter(s => s.status === 'pending').length}
            </div>
            <div className="text-xs text-gray-600">Pending</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">
              {steps.filter(s => s.status === 'error').length}
            </div>
            <div className="text-xs text-gray-600">Errors</div>
          </div>
        </div>
      </div>
    </div>
  );
};